// Alcohol PAR starter rules — deterministic, no model.
//
// Given a restaurant/bar inventory item, classify it as wine / liquor / beer /
// draft beer / RTD (or unknown) and propose practical STARTER values the user
// can edit: a count unit, purchase unit, case pack, a suggested starting
// PAR, a rounding rule, a confidence level, and a plain-English explanation.
//
// Core principle (from the product spec):
//   PAR is the desired on-hand level. Once real usage history (orders / counts /
//   POS) exists, usage-based PAR should replace these category starter values —
//   these are only a sensible first guess.
//
// These rules deliberately propose NO minimum order quantities. Vendor
// minimums live on purchase_options (moq_packages) and are enforced in Savings
// Review / PO validation, never per item. The typical case pack (12 bottles of
// wine, 24 cans of beer) is kept as `case_pack` — descriptive pack knowledge
// that shapes the starter PAR and its rounding rule, not a minimum.
//
// Pure (no React / no I/O) so it is unit-testable and can later be reused by the
// template-create edge function.

export const ALCOHOL_CATEGORIES = ['wine', 'liquor', 'beer', 'draft_beer', 'rtd'];

export const PRETTY_CATEGORY = {
  wine: 'Wine',
  liquor: 'Liquor',
  beer: 'Beer',
  draft_beer: 'Draft beer',
  rtd: 'Seltzer / RTD',
  unknown: 'Unknown',
};

// ── Text helpers ─────────────────────────────────────────────────────────────
function normalize(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents: rosé -> rose
    .toLowerCase()
    .replace(/[^a-z0-9/.\s-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenSet(haystack) {
  return new Set(normalize(haystack).split(/[\s/.-]+/).filter(Boolean));
}

// Short / ambiguous keywords (gin, rum, rye, ale, ipa, port…) must match a whole
// WORD so "ginger", "drum", "rye bread"→ok-ish, "kale", "import" don't trip them.
// Multi-word or digit-bearing keywords ("triple sec", "1/2 bbl", "by the glass")
// are matched as substrings of the normalized string instead.
function makeMatcher(haystack) {
  const norm = normalize(haystack);
  const words = tokenSet(haystack);
  return (keyword) => {
    const k = normalize(keyword);
    if (!k) return false;
    if (/[\s/]/.test(k) || /\d/.test(k)) return norm.includes(k);
    return words.has(k);
  };
}

function anyMatch(matcher, keywords) {
  return keywords.some((k) => matcher(k));
}

// ── Keyword vocabularies ─────────────────────────────────────────────────────
const DRAFT_KEYWORDS = ['keg', 'sixtel', 'bbl', '1/2 bbl', '1/6 bbl', '1/4 bbl', 'half barrel', 'quarter barrel', 'pony keg', 'half bbl', 'sixth barrel'];

const RTD_KEYWORDS = ['seltzer', 'cider', 'rtd', 'hard tea', 'hard seltzer', 'hard lemonade', 'hard kombucha', 'canned cocktail', 'ready to drink', 'spiked', 'high noon', 'white claw', 'truly', 'twisted tea'];

const BEER_KEYWORDS = ['beer', 'lager', 'ipa', 'dipa', 'pilsner', 'pils', 'stout', 'ale', 'cerveza', 'porter', 'pale ale', 'hefeweizen', 'hefe', 'witbier', 'wit', 'saison', 'kolsch', 'lambic', 'bock', 'dunkel', 'marzen', 'gose', 'helles', 'pale lager', 'amber ale', 'brown ale', 'double ipa', 'session ipa', 'n/a beer'];

// Macro / well-known beer brand names carry no style word, so a line that just
// says "Budweiser 24 pack" or "Modelo Especial" still needs to classify as beer.
// Kept to distinctive tokens to avoid colliding with food/vendor names.
const BEER_BRANDS = ['budweiser', 'bud', 'coors', 'miller', 'corona', 'modelo', 'heineken', 'stella', 'michelob', 'busch', 'pabst', 'pbr', 'yuengling', 'guinness', 'pacifico', 'tecate', 'dos equis', 'blue moon', 'rolling rock', 'labatt', 'molson', 'sapporo', 'asahi', 'peroni', 'amstel', 'genesee', 'natural light', 'keystone', 'high life', 'lagunitas', 'sam adams', 'samuel adams', 'sierra nevada'];

const WINE_KEYWORDS = ['wine', 'vino', 'cabernet', 'cab sauv', 'chardonnay', 'chard', 'pinot', 'pinot noir', 'pinot grigio', 'pinot gris', 'merlot', 'sauvignon', 'sauv blanc', 'rose', 'prosecco', 'champagne', 'brut', 'riesling', 'malbec', 'syrah', 'shiraz', 'zinfandel', 'zin', 'grigio', 'grenache', 'sangiovese', 'tempranillo', 'nebbiolo', 'montepulciano', 'moscato', 'gewurztraminer', 'viognier', 'chenin', 'albarino', 'red blend', 'white blend', 'red wine', 'white wine', 'sparkling wine', 'bordeaux', 'burgundy', 'chianti', 'rioja', 'cava', 'cremant', 'sancerre', 'gruner', 'verdejo', 'lambrusco', 'port', 'sherry', 'madeira', 'sauternes'];

const LIQUOR_KEYWORDS = ['vodka', 'gin', 'tequila', 'mezcal', 'rum', 'whiskey', 'whisky', 'bourbon', 'rye', 'scotch', 'brandy', 'cognac', 'liqueur', 'amaro', 'vermouth', 'triple sec', 'schnapps', 'aperitif', 'aperol', 'campari', 'sambuca', 'absinthe', 'grappa', 'pisco', 'cachaca', 'single malt', 'armagnac', 'calvados', 'curacao', 'creme de', 'limoncello', 'irish cream', 'london dry', 'genever', 'aquavit', 'soju', 'bitters', 'st germain', 'chartreuse', 'maraschino', 'kahlua', 'baileys', 'jagermeister', 'fernet', 'blanco', 'reposado', 'anejo', 'silver tequila'];

// Sub-type signal words.
const WINE_HOUSE_SIGNALS = ['house', 'btg', 'by the glass', 'by-the-glass', 'well', 'bulk', 'jug', 'box wine', 'boxed', 'bag in box', 'bib', 'tap wine', '1.5l', '1.5 l', '3l', '3 l', 'magnum pour', 'pour'];
const WINE_PREMIUM_SIGNALS = ['reserve', 'reserva', 'grand cru', 'premier cru', '1er cru', 'gran reserva', 'single vineyard', 'vintage', 'library', 'allocated', 'rare', 'collector', 'first growth', 'grand vin', 'magnum', 'cult'];

const LIQUOR_SPECIALTY_SIGNALS = ['liqueur', 'amaro', 'vermouth', 'triple sec', 'aperol', 'campari', 'creme de', 'schnapps', 'sambuca', 'curacao', 'maraschino', 'st germain', 'chartreuse', 'bitters', 'aperitif', 'limoncello', 'fernet', 'absinthe', 'kahlua', 'baileys', 'irish cream', 'jagermeister'];
const LIQUOR_PREMIUM_SIGNALS = ['reserve', 'xo', 'vsop', 'vintage', 'single barrel', 'single malt', 'small batch', 'small-batch', 'aged', 'cask strength', 'barrel proof', 'limited', 'rare', 'extra anejo', 'extra-anejo', '15 year', '18 year', '21 year', '12 year', '10 year', 'gran', 'top shelf', 'top-shelf'];
const LIQUOR_WELL_SIGNALS = ['well', 'rail', 'house pour', 'speed rail'];

// High-volume packaged beer signals (macro domestic / import brands + cues).
const BEER_HIGH_VOLUME_SIGNALS = ['domestic', 'import', 'bud', 'budweiser', 'bud light', 'coors', 'miller', 'corona', 'modelo', 'heineken', 'stella', 'michelob', 'mich ultra', 'busch', 'pbr', 'pabst', 'yuengling', 'blue moon', 'dos equis', 'pacifico', 'tecate', 'lite', 'high life', 'natural light', 'natty', 'guinness'];
const BEER_CRAFT_SIGNALS = ['craft', 'ipa', 'dipa', 'double ipa', 'imperial', 'stout', 'porter', 'sour', 'barrel aged', 'barrel-aged', 'small batch', 'hazy', 'session', 'gose', 'saison', 'lambic', 'wild', 'farmhouse', 'nitro'];
const DRAFT_HIGH_VOLUME_SIGNALS = [...BEER_HIGH_VOLUME_SIGNALS, '1/2 bbl', 'half barrel', 'half bbl'];

const CAN_SIGNALS = ['can', 'cans', 'canned', 'tallboy', 'pounder'];
const BOTTLE_SIGNALS = ['bottle', 'btl', 'bottles', 'longneck'];

// ── Field extraction ─────────────────────────────────────────────────────────
function readField(item, ...keys) {
  for (const k of keys) {
    const v = item && item[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  }
  return '';
}

function itemName(item) {
  return readField(item, 'item_name', 'description', 'name');
}

// The text we classify over: name + category + brand. Vendor is deliberately
// excluded — a wine distributor also sells liquor and beer, so the vendor name
// would mis-tag everything it supplies.
function classificationHaystack(item) {
  return [itemName(item), readField(item, 'category'), readField(item, 'brand')].join(' ');
}

// A wider haystack (adds unit + pack_size) for container / pack detection.
function packagingHaystack(item) {
  return [itemName(item), readField(item, 'unit'), readField(item, 'pack_size'), readField(item, 'category')].join(' ');
}

const KNOWN_PACKS = new Set([4, 6, 8, 12, 15, 18, 20, 24, 30, 36]);

// Try to read a case / multipack size from the name, pack_size or unit field.
function detectCasePack(item) {
  const hay = normalize(packagingHaystack(item));
  const patterns = [
    /case of (\d{1,2})/,
    /(\d{1,2})\s*(?:pk|pack)\b/,
    /(\d{1,2})\s*x\b/,
    /(\d{1,2})\s*[-/]\s*(?:pack|ct|count)/,
    /(\d{1,2})\s*(?:ct|count)\b/,
    /(\d{1,2})\s*\/\s*\d/, // "24/12oz"
  ];
  for (const re of patterns) {
    const m = hay.match(re);
    if (m) {
      const n = Number(m[1]);
      if (KNOWN_PACKS.has(n)) return n;
    }
  }
  return null;
}

function detectCountUnit(item, fallback) {
  const m = makeMatcher(packagingHaystack(item));
  if (anyMatch(m, CAN_SIGNALS)) return 'can';
  if (anyMatch(m, BOTTLE_SIGNALS)) return 'bottle';
  return fallback;
}

// ── Classification ───────────────────────────────────────────────────────────
/**
 * Classify an item into an alcohol category. Draft beer wins over packaged beer
 * (a keg is a keg), and seltzer/cider/RTD is split out from beer so its rule-3
 * behaviour reads clearly, even though both are case-purchased.
 * @returns {{category: string, matched: string[]}}
 */
export function classifyAlcohol(item) {
  const match = makeMatcher(classificationHaystack(item));
  const draft = makeMatcher([classificationHaystack(item), readField(item, 'unit'), readField(item, 'pack_size')].join(' '));

  if (anyMatch(draft, DRAFT_KEYWORDS)) {
    return { category: 'draft_beer', matched: DRAFT_KEYWORDS.filter((k) => draft(k)) };
  }
  if (anyMatch(match, RTD_KEYWORDS)) {
    return { category: 'rtd', matched: RTD_KEYWORDS.filter((k) => match(k)) };
  }
  if (anyMatch(match, WINE_KEYWORDS)) {
    return { category: 'wine', matched: WINE_KEYWORDS.filter((k) => match(k)) };
  }
  if (anyMatch(match, LIQUOR_KEYWORDS)) {
    return { category: 'liquor', matched: LIQUOR_KEYWORDS.filter((k) => match(k)) };
  }
  if (anyMatch(match, BEER_KEYWORDS) || anyMatch(match, BEER_BRANDS)) {
    return { category: 'beer', matched: [...BEER_KEYWORDS, ...BEER_BRANDS].filter((k) => match(k)) };
  }
  return { category: 'unknown', matched: [] };
}

// ── Per-category proposals ───────────────────────────────────────────────────
function clampConfidence(n) {
  return Math.max(0, Math.min(1, Math.round(n * 100) / 100));
}

function proposeWine(item) {
  const m = makeMatcher(classificationHaystack(item));
  const premium = anyMatch(m, WINE_PREMIUM_SIGNALS);
  const house = anyMatch(m, WINE_HOUSE_SIGNALS);

  if (premium && !house) {
    // Expensive / rare bottle-list wine: counted and bought by the single bottle.
    return {
      alcohol_category: 'wine',
      subtype: 'bottle_list_premium',
      count_unit: 'bottle',
      container: '750 ml bottle',
      purchase_unit: 'bottle',
      case_pack: null,
      starter_par: 2,
      starter_par_range: [1, 2],
      par_unit: 'bottle',
      rounding_rule: 'none',
      confidence: clampConfidence(0.55),
      reason: 'Looks like an expensive or rare bottle-list wine, so it is counted and ordered one bottle at a time — no case rounding. Starter PAR 1–2 bottles; adjust once usage history exists.',
    };
  }

  // Default + explicit house / by-the-glass wine: counted by the bottle, bought
  // by the 12-bottle case, PAR rounded up to whole cases.
  const label = house ? 'House or by-the-glass wine' : 'Wine';
  return {
    alcohol_category: 'wine',
    subtype: house ? 'house_btg' : 'wine_default',
    count_unit: 'bottle',
    container: '750 ml bottle',
    purchase_unit: 'case',
    case_pack: 12,
    starter_par: 12,
    starter_par_range: [12, 24],
    par_unit: 'bottle',
    rounding_rule: 'round_to_case',
    confidence: clampConfidence(house ? 0.62 : 0.55),
    reason: `${label}: counted by the bottle, usually bought by the 12-bottle case. Starter PAR one case (12 bottles), rounded up to whole cases. If this is a pricey bottle-list wine, lower the PAR.`,
  };
}

function proposeLiquor(item) {
  const m = makeMatcher(classificationHaystack(item));
  const specialty = anyMatch(m, LIQUOR_SPECIALTY_SIGNALS);
  const premium = anyMatch(m, LIQUOR_PREMIUM_SIGNALS);
  const well = anyMatch(m, LIQUOR_WELL_SIGNALS);
  // A 6 or 12 case pack is recorded if the data shows it — descriptive pack
  // knowledge only, never a minimum.
  const casePack = detectCasePack(item);

  let subtype = 'spirit_default';
  let starter = 3;
  let range = [2, 6];
  let confidence = 0.5;
  let note = 'Spirit: counted and replenished by the bottle. Couldn’t tell well vs. call from the name, so this is a conservative starter — raise it for high-volume rail pours.';

  if (specialty) {
    subtype = 'specialty_liqueur';
    starter = 1; range = [1, 1]; confidence = 0.6;
    note = 'Specialty liqueur / slow-moving back-bar bottle: usually one bottle on hand, replenished by the bottle.';
  } else if (premium) {
    subtype = 'premium';
    starter = 2; range = [1, 2]; confidence = 0.55;
    note = 'Premium / top-shelf spirit: counted and ordered by the bottle, typically 1–2 on hand.';
  } else if (well) {
    subtype = 'well';
    starter = 6; range = [6, 12]; confidence = 0.6;
    note = 'Well / rail spirit: high turnover, so a deeper starter PAR (6–12 bottles). Still ordered by the bottle unless vendor history shows case buying.';
  }

  return {
    alcohol_category: 'liquor',
    subtype,
    count_unit: 'bottle',
    container: '750 ml bottle',
    purchase_unit: 'bottle',
    case_pack: casePack,
    starter_par: starter,
    starter_par_range: range,
    par_unit: 'bottle',
    rounding_rule: 'none',
    confidence: clampConfidence(confidence),
    reason: note,
  };
}

function proposeBeerLike(item, category) {
  const m = makeMatcher(classificationHaystack(item));
  const countUnit = detectCountUnit(item, 'can');
  const casePack = detectCasePack(item) ?? (category === 'rtd' ? 24 : 24);
  const packDetected = detectCasePack(item) != null;

  let subtype;
  let starter;
  let range;
  if (category === 'rtd') {
    subtype = 'rtd';
    range = [24, 48];
    starter = 24;
  } else if (anyMatch(m, BEER_HIGH_VOLUME_SIGNALS)) {
    subtype = 'high_volume';
    range = [48, 72];
    starter = 48;
  } else if (anyMatch(m, BEER_CRAFT_SIGNALS)) {
    subtype = 'craft';
    range = [12, 24];
    starter = 12;
  } else {
    subtype = 'normal';
    range = [24, 48];
    starter = 24;
  }
  // Round the starter PAR up to whole cases so it lands on a clean case boundary.
  starter = Math.max(casePack, Math.ceil(starter / casePack) * casePack);

  const kind = category === 'rtd' ? 'Seltzer / RTD' : 'Packaged beer';
  return {
    alcohol_category: category,
    subtype,
    count_unit: countUnit,
    container: `${countUnit === 'can' ? 'individual can' : 'individual bottle'}`,
    purchase_unit: 'case',
    case_pack: casePack,
    starter_par: starter,
    starter_par_range: range,
    par_unit: countUnit,
    rounding_rule: 'round_to_case',
    confidence: clampConfidence(packDetected ? 0.65 : 0.55),
    reason: `${kind}: counted by the ${countUnit}, bought by the case${packDetected ? ` of ${casePack}` : ` (assumed ${casePack}/case — confirm the real pack)`}. Starter PAR ${starter} ${countUnit}s, rounded up to whole cases.`,
  };
}

function proposeDraft(item) {
  const m = makeMatcher([classificationHaystack(item), readField(item, 'unit'), readField(item, 'pack_size')].join(' '));
  const highVolume = anyMatch(m, DRAFT_HIGH_VOLUME_SIGNALS);
  const starter = highVolume ? 2 : 1;
  return {
    alcohol_category: 'draft_beer',
    subtype: highVolume ? 'high_volume' : 'normal',
    count_unit: 'keg',
    container: 'keg',
    purchase_unit: 'keg',
    case_pack: null,
    starter_par: starter,
    starter_par_range: highVolume ? [2, 4] : [1, 2],
    par_unit: 'keg',
    rounding_rule: 'round_to_keg',
    confidence: clampConfidence(0.7),
    reason: `Draft beer: tracked separately from packaged beer because the purchase and count unit is the keg. Starter PAR ${starter} keg${starter === 1 ? '' : 's'}; whole kegs only.`,
  };
}

const UNKNOWN_PROPOSAL = {
  alcohol_category: 'unknown',
  subtype: 'unknown',
  count_unit: 'each',
  container: null,
  purchase_unit: 'bottle',
  case_pack: null,
  starter_par: null,
  starter_par_range: null,
  par_unit: 'each',
  rounding_rule: 'none',
  confidence: 0,
  reason: 'Not recognized as wine, liquor, beer, draft, or RTD — no starter PAR applied. Tell me the category or rename the item if it is alcohol.',
};

/**
 * Produce the full starter-PAR proposal for a single item. The shape matches the
 * product spec's output object plus a few transparency extras (subtype,
 * starter_par_range, container).
 */
export function proposeAlcoholPar(item) {
  const name = itemName(item) || 'Item';
  const { category } = classifyAlcohol(item);
  let body;
  switch (category) {
    case 'wine': body = proposeWine(item); break;
    case 'liquor': body = proposeLiquor(item); break;
    case 'beer': body = proposeBeerLike(item, 'beer'); break;
    case 'rtd': body = proposeBeerLike(item, 'rtd'); break;
    case 'draft_beer': body = proposeDraft(item); break;
    default: body = { ...UNKNOWN_PROPOSAL }; break;
  }
  return { item_name: name, ...body };
}

/**
 * Classify + propose over a list of items. Splits the results into alcohol
 * (something we can set) vs skipped (unknown), with a per-category count so the
 * chat can say "12 wine, 8 liquor, 5 beer".
 */
export function proposeParsForItems(items) {
  const proposals = (items || []).map((it) => ({ item: it, proposal: proposeAlcoholPar(it) }));
  const counts = { wine: 0, liquor: 0, beer: 0, draft_beer: 0, rtd: 0, unknown: 0 };
  const alcohol = [];
  const skipped = [];
  for (const row of proposals) {
    const cat = row.proposal.alcohol_category;
    counts[cat] = (counts[cat] || 0) + 1;
    if (cat === 'unknown') skipped.push(row);
    else alcohol.push(row);
  }
  return { proposals, alcohol, skipped, counts, alcoholCount: alcohol.length };
}

/** Human breakdown like "12 wine, 8 liquor, 5 beer" (omits zero categories). */
export function describeCounts(counts) {
  const order = ['wine', 'liquor', 'beer', 'draft_beer', 'rtd'];
  const parts = [];
  for (const c of order) {
    if (counts && counts[c] > 0) parts.push(`${counts[c]} ${PRETTY_CATEGORY[c].toLowerCase()}`);
  }
  return parts.join(', ');
}

// ── Counter Intelligence intent detection ────────────────────────────────────
// Whenever PARs come up — a request to fill them in ("add / set / apply / suggest
// pars") OR a question about them ("how do I add pars?", "what are pars?") — we
// route to the PAR-suggestion offer, which itself points out it can only do
// beverages. The ONE thing left to the normal template-edit path is an explicit
// single value ("set par to 5 for the wine"), which is a specific edit, not a
// request for AI suggestions.
const PAR_WORD_RE = /\bpars?\b/i;
// "set par to 5", "par = 12", "par at 6" → a specific value, not the AI flow.
const EXPLICIT_PAR_VALUE_RE = /\bpars?\b\s*(?:to|=|at|of|:)?\s*\d/i;
// Verbs that mean "fill these in for me". `create`/`build`/`make` are excluded so
// "create a template … with pars" still routes to template creation.
const PAR_ACTION_VERB_RE = /\b(set|add|apply|suggest|recommend|propose|fill|populate|generate|assign|figure|calculate|estimate|guess|do|put|give|update|fix|complete|finish|auto[\s-]?par|autoset|come up with)\b/i;
// Question phrasings about PARs — these should also surface the offer (and point
// out it's beverages-only) rather than a generic how-to.
const PAR_QUESTION_RE = /\b(how (?:do|can|would|to|should)|where (?:do|can|is|are)|what(?:'?s| is| are| do)|why|can you|could you|should i|help me|need to)\b/i;

/**
 * True when the user is talking about PARs in a way that should surface the
 * AI PAR-suggestion offer — either asking us to fill them in, or asking how/what
 * PARs are. Returns false only for an explicit single-value edit (left to the
 * normal template-edit path) or when PARs aren't mentioned at all.
 */
export function matchSetParsIntent(text) {
  const t = String(text || '');
  if (!PAR_WORD_RE.test(t)) return false;
  if (EXPLICIT_PAR_VALUE_RE.test(t)) return false;
  return PAR_ACTION_VERB_RE.test(t) || PAR_QUESTION_RE.test(t);
}

// ── Template name matching ───────────────────────────────────────────────────
// When the user names a template in the command ("add pars to ordering - liquor
// template"), figure out which of their ordering templates they mean. Pure so it
// can be unit-tested; the drawer feeds it the store's template list.
function normalizeTemplateText(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[—–]/g, ' ')              // em / en dash → space
    .replace(/\btemplates?\b/g, ' ')    // drop the word "template(s)"
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Generic words that don't distinguish one ordering template from another.
const TEMPLATE_STOP_WORDS = new Set([
  'ordering', 'order', 'orders', 'the', 'a', 'an', 'for', 'to', 'in', 'on', 'add',
  'set', 'put', 'do', 'apply', 'fill', 'suggest', 'pars', 'par', 'moq', 'moqs',
  'of', 'all', 'beverage', 'beverages', 'and', 'please', 'this', 'that', 'my',
  'can', 'you', 'me', 'with', 'level', 'levels', 'value', 'values',
]);

/**
 * Score each template against the command and return the best match(es). Returns
 * [] when nothing meaningfully matches (the caller then asks which template), one
 * template on a clear win, or several on a tie. `templates` is [{id, name}].
 */
export function matchTemplatesToCommand(command, templates) {
  const cmd = normalizeTemplateText(command);
  if (!cmd || !Array.isArray(templates) || !templates.length) return [];
  const scored = templates.map((t) => {
    const fullName = normalizeTemplateText(t.name);
    const charTokens = fullName.split(' ').filter((w) => w && !TEMPLATE_STOP_WORDS.has(w));
    let score = 0;
    if (fullName && cmd.includes(fullName)) score += 6;                 // whole name appears
    const charPhrase = charTokens.join(' ');
    if (charPhrase && cmd.includes(charPhrase)) score += 4;             // distinctive phrase appears
    score += charTokens.filter((tok) => new RegExp(`\\b${tok}\\b`).test(cmd)).length; // distinctive words
    return { template: t, score };
  });
  const max = Math.max(0, ...scored.map((s) => s.score));
  if (max <= 0) return [];
  return scored.filter((s) => s.score === max).map((s) => s.template);
}

// ── Apply: build set_par operations for the count-creation editor ────────────
// The editor's applyAiOpsToRows understands set_par ops whose resolution
// carries { updates:[{inventory_item_id, location}], par_value } (and optional
// par_unit). We group rows that share the same value+unit into one op to keep
// the op list small, but correctness does not depend on the grouping.
// set_moq is no longer EMITTED here — vendor minimums live on purchase_options
// — though the editor's apply hook stays tolerant of stored set_moq ops.
function numericOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function buildSetParMoqOps(items) {
  const parGroups = new Map();
  for (const it of items || []) {
    const invId = it.inventory_item_id ?? it.id;
    if (invId == null) continue;
    const update = { inventory_item_id: invId, location: it.location || '', item_name: it.item_name || '' };

    const par = numericOrNull(it.par_level);
    if (par != null && par >= 0) {
      const unit = it.par_unit || 'each';
      const key = `${par}::${unit}`;
      if (!parGroups.has(key)) parGroups.set(key, { par_value: par, par_unit: unit, updates: [] });
      parGroups.get(key).updates.push(update);
    }
  }

  const ops = [];
  for (const g of parGroups.values()) {
    ops.push({
      type: 'set_par',
      description: `Set PAR to ${g.par_value} ${g.par_unit} for ${g.updates.length} item${g.updates.length === 1 ? '' : 's'}`,
      resolution: { updates: g.updates, par_value: g.par_value, par_unit: g.par_unit },
    });
  }
  return ops;
}
